import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerAuthService } from '@/services/auth';
import { useConfig, useTheme } from '@connor-music/shared';
import { useCustomerAuth } from '@/hooks/useAuth';
import { Mail } from 'lucide-react';

type LoginStep = 'email' | 'code';

export default function Login() {
  const { config } = useConfig();
  const { resolvedTheme } = useTheme();
  const navigate = useNavigate();
  const { login } = useCustomerAuth();
  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);

  const logoUrl = resolvedTheme === 'dark' ? config.logo_dark_url : config.logo_light_url;

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await customerAuthService.requestCode(email);

      if (result.success) {
        setStep('code');
        setCodeSent(true);
      } else {
        setError(result.error || 'Failed to send code');
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await customerAuthService.verifyCode(email, code);
      if (result.success) {
        login();
        navigate('/', { replace: true });
      } else {
        setError(result.error || 'Verification failed');
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    setIsSubmitting(true);
    setError(null);
    setCode('');

    try {
      const result = await customerAuthService.requestCode(email);
      if (result.success) {
        setCodeSent(true);
        setError(null);
      } else {
        setError(result.error || 'Failed to resend code');
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setCode('');
    setError(null);
    setCodeSent(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-8 sm:py-12">
      {/* Card */}
      <div className="w-full max-w-sm sm:max-w-md">
        <div className="rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 px-6 py-8 sm:px-10 sm:py-10">
          {/* Logo & Header */}
          <div className="text-center">
            {logoUrl ? (
              <div className="mb-6 flex justify-center">
                <img
                  src={logoUrl}
                  alt={config.portal_name || 'Connor Music Audit'}
                  style={{ width: config.login_logo_width || 200 }}
                  className="h-auto"
                />
              </div>
            ) : (
              <div className="mb-6 flex justify-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/50">
                  <Mail className="h-7 w-7 text-primary-600 dark:text-primary-400" />
                </div>
              </div>
            )}
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">
              {config.portal_name || 'Connor Music Audit'}
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Customer Portal
            </p>
          </div>

          {/* Divider */}
          <div className="my-6 border-t border-gray-200 dark:border-gray-700" />

          {/* Access notice */}
          <div className="mb-6 rounded-lg bg-gray-50 dark:bg-gray-700/50 px-4 py-3">
            <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300">
              This portal is for authorized audit participants only.
              Sign in with the email address provided by Connor Consulting.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6 rounded-lg bg-error-50 dark:bg-error-700/20 border border-error-200 dark:border-error-700 px-4 py-3">
              <p className="text-sm text-error-600 dark:text-error-500">{error}</p>
            </div>
          )}

          {/* Email step */}
          {step === 'email' && (
            <form onSubmit={handleRequestCode} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  placeholder="you@company.com"
                  className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white px-3 py-2.5 shadow-sm focus:border-accent-500 focus:ring-1 focus:ring-accent-500 sm:text-sm"
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  We'll send a one-time login code to your email.
                </p>
              </div>
              <button
                type="submit"
                disabled={isSubmitting || !email}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-accent-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50"
              >
                <Mail className="h-4 w-4" />
                {isSubmitting ? 'Sending code...' : 'Send Login Code'}
              </button>
            </form>
          )}

          {/* Code verification step */}
          {step === 'code' && (
            <form onSubmit={handleVerifyCode} className="space-y-5">
              {codeSent && (
                <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-4 py-3">
                  <p className="text-sm text-green-700 dark:text-green-400">
                    A login code has been sent to <strong>{email}</strong>
                  </p>
                </div>
              )}
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Enter the 6-digit code from your email
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  required
                  autoFocus
                  placeholder="000000"
                  className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white px-3 py-2.5 shadow-sm focus:border-accent-500 focus:ring-1 focus:ring-accent-500 text-center text-2xl tracking-widest"
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting || code.length !== 6}
                className="w-full flex justify-center rounded-lg bg-accent-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50"
              >
                {isSubmitting ? 'Verifying...' : 'Verify & Sign In'}
              </button>
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={handleBackToEmail}
                  className="text-accent-600 hover:text-accent-500 dark:text-accent-400 transition-colors"
                >
                  Use a different email
                </button>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isSubmitting}
                  className="text-accent-600 hover:text-accent-500 dark:text-accent-400 disabled:opacity-50 transition-colors"
                >
                  Resend code
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 flex flex-col items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
          {(config.terms_url || config.privacy_url) && (
            <div className="flex items-center gap-3">
              {config.terms_url && (
                <a href={config.terms_url} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                  Terms of Use
                </a>
              )}
              {config.terms_url && config.privacy_url && (
                <span aria-hidden="true">&middot;</span>
              )}
              {config.privacy_url && (
                <a href={config.privacy_url} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                  Privacy Policy
                </a>
              )}
            </div>
          )}
          <p>&copy; {new Date().getFullYear()} Connor Consulting Corporation</p>
        </div>
      </div>
    </div>
  );
}
